import { MetadataFieldType } from '@/api/generatedApi';
import xlsx from 'node-xlsx';
import {
  BulkUploadData,
  DuplicateAssetWarning,
  DuplicateMetadataColumnWarning,
  MetadataFieldDefinition,
  ParsedMetadataRow,
} from '../types';

export const ERRORS = {
  FILE_SIZE_EXCEEDED: 'File size exceeds maximum limit of 10MB',
  UNSUPPORTED_FORMAT: 'Unsupported file format. Please use CSV, XLSX, or XLS files.',
  NO_WORKSHEETS: 'No worksheets found in the file',
  INSUFFICIENT_DATA: 'File must contain at least a header row and one data row',
  PATH_COLUMN_MISSING: 'Required column "path" not found. Please ensure your file has an "path" column.',
  NO_METADATA_FIELDS: 'No metadata fields found. Please include at least one metadata column.',
  NO_VALID_ROWS:
    'No valid data rows found. Please ensure you have asset paths and at least one metadata value per row.',
  PARSE_FAILED: 'Failed to parse file. Please ensure it is a valid spreadsheet file.',
} as const;

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ASSET_PATH_COLUMN = 'path';
const ASSET_ID_COLUMN = 'id';
const METADATA_PREFIX = 'metadata:';

export const parseSpreadsheetFile = async (file: File): Promise<BulkUploadData> => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(ERRORS.FILE_SIZE_EXCEEDED);
  }

  const fileExtension = file.name.toLowerCase().split('.').pop();

  if (!fileExtension || !['csv', 'xlsx', 'xls'].includes(fileExtension)) {
    throw new Error(ERRORS.UNSUPPORTED_FORMAT);
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const worksheets = xlsx.parse(arrayBuffer);

    if (!worksheets || worksheets.length === 0) {
      throw new Error(ERRORS.NO_WORKSHEETS);
    }

    const firstWorksheet = worksheets[0];
    const jsonData = firstWorksheet.data as string[][];

    if (jsonData.length < 2) {
      throw new Error(ERRORS.INSUFFICIENT_DATA);
    }

    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);

    const assetPathColumnIndex = headers.findIndex(header => header?.trim() === ASSET_PATH_COLUMN);
    if (assetPathColumnIndex === -1) {
      throw new Error(ERRORS.PATH_COLUMN_MISSING);
    }

    const assetIdColumnIndex = headers.findIndex(header => header?.trim() === ASSET_ID_COLUMN);

    const metadataColumnMap = new Map<string, number>();
    const duplicateMetadataColumnWarnings: DuplicateMetadataColumnWarning[] = [];
    const uniqueMetadataFields: string[] = [];

    for (const [colIndex, header_] of headers.entries()) {
      const header = header_?.trim();
      if (header.startsWith(METADATA_PREFIX)) {
        const fieldName = header.slice(METADATA_PREFIX.length);
        const existingColumnIndex = metadataColumnMap.get(fieldName.toLowerCase());

        if (existingColumnIndex === undefined) {
          metadataColumnMap.set(fieldName.toLowerCase(), colIndex + 1);
          uniqueMetadataFields.push(fieldName);
        } else {
          let warning = duplicateMetadataColumnWarnings.find(
            w => w.fieldName.toLowerCase() === fieldName.toLowerCase()
          );
          if (!warning) {
            warning = {
              fieldName: fieldName,
              firstColumnIndex: existingColumnIndex,
              duplicateColumnIndexes: [],
            };
            duplicateMetadataColumnWarnings.push(warning);
          }
          warning.duplicateColumnIndexes.push(colIndex + 1);
        }
      }
    }

    if (uniqueMetadataFields.length === 0) {
      throw new Error(ERRORS.NO_METADATA_FIELDS);
    }

    const parsedData: ParsedMetadataRow[] = [];

    for (const [index, row] of dataRows.entries()) {
      const assetPath = row[assetPathColumnIndex]?.toString().trim();
      const assetId = assetIdColumnIndex === -1 ? undefined : row[assetIdColumnIndex]?.toString().trim();

      if (!assetPath) {
        continue;
      }

      const metadata: Record<string, string> = {};
      const usedMetadataFields = new Set<string>();

      for (const [colIndex, header_] of headers.entries()) {
        const header = header_?.trim();
        const value = row[colIndex]?.toString().trim();

        if (header && header.startsWith(METADATA_PREFIX) && value) {
          const fieldName = header.slice(METADATA_PREFIX.length);
          const fieldNameLower = fieldName.toLowerCase();

          if (!usedMetadataFields.has(fieldNameLower)) {
            metadata[fieldName] = value;
            usedMetadataFields.add(fieldNameLower);
          }
        }
      }

      if (Object.keys(metadata).length > 0) {
        parsedData.push({
          assetName: assetPath,
          assetId: assetId || undefined,
          metadata,
          rowIndex: index + 2,
        });
      }
    }

    if (parsedData.length === 0) {
      throw new Error(ERRORS.NO_VALID_ROWS);
    }

    const seenAssets = new Map<string, number>();
    const duplicateWarnings: DuplicateAssetWarning[] = [];
    const deduplicatedData: ParsedMetadataRow[] = [];

    for (const row of parsedData) {
      const assetNameLower = row.assetName.toLowerCase();
      const existingRowIndex = seenAssets.get(assetNameLower);

      if (existingRowIndex === undefined) {
        seenAssets.set(assetNameLower, row.rowIndex);
        deduplicatedData.push(row);
      } else {
        let warning = duplicateWarnings.find(w => w.assetName.toLowerCase() === assetNameLower);
        if (!warning) {
          warning = {
            assetName: row.assetName,
            firstRowIndex: existingRowIndex,
            duplicateRowIndexes: [],
          };
          duplicateWarnings.push(warning);
        }
        warning.duplicateRowIndexes.push(row.rowIndex);
      }
    }

    const metadataFieldDefinitions: MetadataFieldDefinition[] = uniqueMetadataFields.map(fieldName => ({
      name: fieldName,
      type: MetadataFieldType.Text,
      isNew: true,
    }));

    return {
      file,
      parsedData: deduplicatedData,
      metadataFields: uniqueMetadataFields,
      metadataFieldDefinitions,
      duplicateWarnings: duplicateWarnings.length > 0 ? duplicateWarnings : undefined,
      duplicateMetadataColumnWarnings:
        duplicateMetadataColumnWarnings.length > 0 ? duplicateMetadataColumnWarnings : undefined,
      assetMatches: deduplicatedData.map(row => ({
        assetName: row.assetName,
        assetId: row.assetId,
        searchResults: [],
        status: 'pending' as const,
      })),
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(ERRORS.PARSE_FAILED);
  }
};
